// audit-safety-integrated
import { validatedFetch, readJson, safeErrorMessage } from '../utils/auditSafety';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

const TN_DISTRICTS = [
  "Ariyalur", "Chengalpattu", "Chennai", "Coimbatore", "Cuddalore",
  "Dharmapuri", "Dindigul", "Erode", "Kallakurichi", "Kancheepuram",
  "Kanyakumari", "Karur", "Krishnagiri", "Madurai", "Mayiladuthurai",
  "Nagapattinam", "Namakkal", "Nilgiris", "Perambalur", "Pudukkottai",
  "Ramanathapuram", "Ranipet", "Salem", "Sivaganga", "Tenkasi",
  "Thanjavur", "Theni", "Thoothukudi", "Tiruchirappalli", "Tirunelveli",
  "Tirupathur", "Tiruppur", "Tiruvallur", "Tiruvannamalai", "Tiruvarur",
  "Vellore", "Villupuram", "Virudhunagar"
];

const CATEGORY_LABELS = {
  food: 'Food',
  dailie: 'Daily',
  housekeeping: 'Housekeeping',
};

const ItemRow = ({ item, checked, onToggle, jpcPrice }) => (
  <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0">
    <input type="checkbox" checked={!!checked} onChange={onToggle} className="accent-[#1978B5] flex-shrink-0" />
    <span className={`text-xs leading-snug flex-1 ${checked ? 'text-[#1978B5] font-medium' : 'text-gray-700'}`}>
      {item.name}
    </span>
    {jpcPrice !== undefined && (
      <span className="text-xs font-semibold text-green-600 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded flex-shrink-0">
        ₹{jpcPrice}
      </span>
    )}
    <span className="text-xs text-gray-400 flex-shrink-0">{item.indent || item.unit}</span>
  </label>
);

const ItemPanel = ({ districtName, selectedSet, onToggle, onSelectAll, search, setSearch, isEdit, groupedItems, getFilteredItems, jpcPrices }) => {
  const filtered       = getFilteredItems(search);
  const totalSelected  = selectedSet ? selectedSet.size : 0;
  const districtPrices = isEdit ? (jpcPrices[districtName] || {}) : {};

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <p className="text-sm font-semibold text-[#1978B5]">{districtName === 'state' ? 'State-wide Items' : districtName}</p>
        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{totalSelected} selected</span>
      </div>

      <input
        type="text"
        placeholder="Search items..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm mb-2 focus:outline-none focus:ring-1 focus:ring-[#1978B5] flex-shrink-0"
      />

      <div className="flex-1 overflow-y-auto border border-gray-100 rounded-lg min-h-0">
        {filtered ? (
          <div>
            {filtered.length === 0 ? (
              <p className="text-center text-gray-400 text-xs py-6">No items found</p>
            ) : (
              <>
                <div className="sticky top-0 bg-gray-50 border-b border-gray-100 px-3 py-1.5 flex items-center justify-between z-10">
                  <span className="text-xs text-gray-500">{filtered.length} results</span>
                  <button onClick={() => onSelectAll(districtName, filtered, isEdit)} className="text-xs text-[#1978B5] hover:underline">
                    {filtered.every(i => selectedSet?.has(i.id)) ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                {filtered.map(item => (
                  <ItemRow key={item.id} item={item} checked={selectedSet?.has(item.id)} onToggle={() => onToggle(districtName, item.id, isEdit)} jpcPrice={districtPrices[item.id]} />
                ))}
              </>
            )}
          </div>
        ) : (
          Object.entries(groupedItems).map(([cat, catItems]) => (
            <div key={cat}>
              <div className="sticky top-0 bg-gray-50 border-b border-gray-100 px-3 py-1.5 flex items-center justify-between z-10">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  {CATEGORY_LABELS[cat] || cat}
                </span>
                <button onClick={() => onSelectAll(districtName, catItems, isEdit)} className="text-xs text-[#1978B5] hover:underline">
                  {catItems.every(i => selectedSet?.has(i.id)) ? 'Deselect' : 'Select all'}
                </button>
              </div>
              {catItems.map(item => (
                <ItemRow key={item.id} item={item} checked={selectedSet?.has(item.id)} onToggle={() => onToggle(districtName, item.id, isEdit)} jpcPrice={districtPrices[item.id]} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const DistrictPanel = ({ districtItemsMap, assignedOthers, activeDist, setActiveDist, onToggle, search, setSearch }) => (
  <div className="col-span-2 flex flex-col p-3 bg-gray-50 min-h-0">
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex-shrink-0">
      Districts
      {Object.keys(districtItemsMap).length > 0 && (
        <span className="ml-2 bg-[#1978B5] text-white rounded-full px-1.5 py-0.5 text-xs normal-case">
          {Object.keys(districtItemsMap).length}
        </span>
      )}
    </p>
    <input
      type="text"
      placeholder="Search..."
      value={search}
      onChange={e => setSearch(e.target.value)}
      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs mb-2 focus:outline-none focus:ring-1 focus:ring-[#1978B5] bg-white flex-shrink-0"
    />
    <div className="flex-1 overflow-y-auto space-y-0.5 min-h-0">
      {TN_DISTRICTS.filter(d => d.toLowerCase().includes(search.toLowerCase())).map(district => {
        const isSelected = districtItemsMap[district] !== undefined;
        const isActive   = activeDist === district;
        const isTaken    = assignedOthers.has(district) && !isSelected;
        const count      = districtItemsMap[district]?.size || 0;
        return (
          <div
            key={district}
            onClick={() => { if (!isTaken && isSelected) setActiveDist(district); }}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all text-xs
              ${isTaken ? 'opacity-40 cursor-not-allowed' : isSelected ? 'cursor-pointer' : 'cursor-default'}
              ${isActive ? 'bg-[#1978B5] text-white' : isSelected ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'text-gray-700'}`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={isTaken}
              onChange={() => onToggle(district)}
              onClick={e => e.stopPropagation()}
              className="accent-[#1978B5] flex-shrink-0 cursor-pointer"
            />
            <span className="truncate font-medium flex-1">{district}</span>
            {isSelected && (
              <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-semibold ${isActive ? 'bg-white text-[#1978B5]' : 'bg-blue-200 text-blue-700'}`}>
                {count}
              </span>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const JpcUser = () => {
  const navigate = useNavigate();


  // ── data ──────────────────────────────────────────────
  const [users, setUsers]   = useState([]);
  const [items, setItems]   = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // ── alerts ────────────────────────────────────────────
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // ── create form ───────────────────────────────────────
  const [form, setForm] = useState({ name: '', usernamePrefix: '' });
  const [jpcType, setJpcType] = useState(''); // 'state' | 'regional' | ''
  const [districtItems, setDistrictItems] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // ── create UI state ───────────────────────────────────
  const [districtSearch, setDistrictSearch] = useState('');
  const [activeDistrict, setActiveDistrict] = useState(null);
  const [itemSearch, setItemSearch] = useState('');

  // ── edit modal ────────────────────────────────────────
  const [editUser, setEditUser] = useState(null);
  const [editDistrictItems, setEditDistrictItems] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editActiveDistrict, setEditActiveDistrict] = useState(null);
  const [editItemSearch, setEditItemSearch] = useState('');
  const [editDistrictSearch, setEditDistrictSearch] = useState('');

  // ── jpc prices per district (for edit modal) ──────────
  const [jpcPrices, setJpcPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);

  // ── delete confirm ────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── UI: show/hide create form + expanded user row ─────
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);

  // ─────────────────────────────────────────────────────
  // helpers
  // ─────────────────────────────────────────────────────

  const parseDistricts = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch { return {}; }
    }
    if (Array.isArray(raw)) {
      return raw.reduce((acc, d) => { acc[d] = []; return acc; }, {});
    }
    if (typeof raw === 'object') return raw;
    return {};
  };

  const buildDistrictsPayload = (map) =>
    Object.fromEntries(
      Object.entries(map).map(([d, set]) => [d, [...set]])
    );

  const getAssignedDistricts = (excludeUserId = null) => {
    const assigned = new Set();
    users.forEach(u => {
      if (excludeUserId && u.id === excludeUserId) return;
      Object.keys(u.districts || {}).forEach(d => assigned.add(d));
    });
    return assigned;
  };

  // ─────────────────────────────────────────────────────
  // fetch
  // ─────────────────────────────────────────────────────

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setError('');
    try {
      const res  = await validatedFetch(`${API_BASE_URL}/user/jpc-staffs`, { headers: {  } });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.message || 'Failed to fetch users');
      const parsed = (data.data || []).map(u => ({
        ...u,
        districts: parseDistricts(u.districts),
      }));
      setUsers(parsed);
    } catch (err) { setError(safeErrorMessage(err)); }
    finally { setLoadingUsers(false); }
  };

  const fetchItems = async () => {
    setLoadingItems(true);
    try {
      const res  = await validatedFetch(`${API_BASE_URL}/item/all/list`, { headers: {  } });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.message || 'Failed to fetch items');
      setItems(data.data || []);
    } catch (err) { setError(safeErrorMessage(err)); }
    finally { setLoadingItems(false); }
  };

  const fetchJpcPricesForUser = async (districtNames) => {
    setLoadingPrices(true);
    const result = {};
    await Promise.all(districtNames.map(async (district) => {
      try {
        const res  = await validatedFetch(`${API_BASE_URL}/item/jpc-rate/${encodeURIComponent(district)}`, {
          headers: {  },
        });
        const data = await readJson(res);
        if (!data.error && Array.isArray(data.data)) {
          const priceMap = {};
          data.data.forEach(item => {
            if (item.rate !== undefined && item.rate !== null && item.rate !== '-') {
              priceMap[item.id] = item.rate;
            }
          });
          result[district] = priceMap;
        }
      } catch (e) { void 0; }
    }));
    setJpcPrices(prev => ({ ...prev, ...result }));
    setLoadingPrices(false);
  };

  useEffect(() => { fetchUsers(); fetchItems(); }, []);

  const handleJpcTypeChange = (type) => {
    setJpcType(type);
    setDistrictItems(type === 'state' ? { state: new Set() } : {});
    setActiveDistrict(type === 'state' ? 'state' : null);
    setItemSearch('');
    setDistrictSearch('');
  };

  // ─────────────────────────────────────────────────────
  // create — district toggle
  // ─────────────────────────────────────────────────────

  const toggleDistrict = (district) => {
    const isSelected = districtItems[district] !== undefined;
    if (isSelected) {
      const next = { ...districtItems };
      delete next[district];
      setDistrictItems(next);
      if (activeDistrict === district) setActiveDistrict(null);
    } else {
      setDistrictItems(prev => {
        // inherit items already selected in any existing district
        const existing = Object.values(prev).find(s => s.size > 0);
        return { ...prev, [district]: existing ? new Set(existing) : new Set() };
      });
      setActiveDistrict(district);
    }
  };

  const toggleItem = (districtName, itemId, isEdit = false) => {
    const setter = isEdit ? setEditDistrictItems : setDistrictItems;
    setter(prev => {
      const cur = new Set(prev[districtName] || []);
      const adding = !cur.has(itemId);
      // apply to all selected districts (create mode only)
      if (!isEdit) {
        const updated = {};
        Object.keys(prev).forEach(d => {
          const s = new Set(prev[d] || []);
          adding ? s.add(itemId) : s.delete(itemId);
          updated[d] = s;
        });
        return { ...prev, ...updated };
      }
      const updated = {};
      Object.keys(prev).forEach(d => {
        const s = new Set(prev[d] || []);
        adding ? s.add(itemId) : s.delete(itemId);
        updated[d] = s;
      });
      return { ...prev, ...updated };
    });
  };

  const selectAllItems = (districtName, filtered, isEdit = false) => {
    const setter = isEdit ? setEditDistrictItems : setDistrictItems;
    setter(prev => {
      const cur = new Set(prev[districtName] || []);
      const allSelected = filtered.every(i => cur.has(i.id));
      // apply to all selected districts (create mode only)
      if (!isEdit) {
        const updated = {};
        Object.keys(prev).forEach(d => {
          const s = new Set(prev[d] || []);
          filtered.forEach(i => allSelected ? s.delete(i.id) : s.add(i.id));
          updated[d] = s;
        });
        return { ...prev, ...updated };
      }
      const updated = {};
      Object.keys(prev).forEach(d => {
        const s = new Set(prev[d] || []);
        filtered.forEach(i => allSelected ? s.delete(i.id) : s.add(i.id));
        updated[d] = s;
      });
      return { ...prev, ...updated };
    });
  };

  // ─────────────────────────────────────────────────────
  // create — submit
  // ─────────────────────────────────────────────────────

  const handleCreate = async () => {
    setError(''); setSuccess('');
    if (!form.name.trim())           return setError('JPC Name is required');
    if (!form.usernamePrefix.trim()) return setError('Username is required');
    if (!jpcType)                    return setError('Select JPC type (State or Regional)');
    if (jpcType === 'state') {
      if (!districtItems['state'] || districtItems['state'].size === 0)
        return setError('Select at least one item');
    } else {
      if (Object.keys(districtItems).length === 0) return setError('Select at least one district');
    }

    setSubmitLoading(true);
    try {
      const res = await validatedFetch(`${API_BASE_URL}/user/jpc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          jpcType === 'state'
            ? {
                name:     form.name.trim(),
                username: `${form.usernamePrefix.trim()}@rcs`,
                jpc_type: 'state',
                items:    [...(districtItems['state'] || [])],
              }
            : {
                name:      form.name.trim(),
                username:  `${form.usernamePrefix.trim()}@rcs`,
                jpc_type:  'regional',
                districts: buildDistrictsPayload(districtItems),
              }
        ),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.message || 'Failed to create user');
      setSuccess('JPC User created successfully');
      setForm({ name: '', usernamePrefix: '' });
      setJpcType('');
      setDistrictItems({});
      setActiveDistrict(null);
      fetchUsers();
    } catch (err) { setError(safeErrorMessage(err)); }
    finally { setSubmitLoading(false); }
  };

  // ─────────────────────────────────────────────────────
  // edit
  // ─────────────────────────────────────────────────────

  const openEdit = (user) => {
    if (user.jpc_type === 'state') {
      const stateItems = new Set(Array.isArray(user.items) ? user.items : []);
      setEditDistrictItems({ state: stateItems });
      setEditActiveDistrict('state');
    } else {
      const map = {};
      Object.entries(user.districts || {}).forEach(([d, ids]) => {
        map[d] = new Set(Array.isArray(ids) ? ids : []);
      });
      setEditDistrictItems(map);
      setEditActiveDistrict(Object.keys(map)[0] || null);
      fetchJpcPricesForUser(Object.keys(map));
    }
    setEditUser(user);
    setEditDistrictSearch('');
    setEditItemSearch('');
    setJpcPrices({});
    setError(''); setSuccess('');
  };

  const toggleEditDistrict = (district) => {
    setEditDistrictItems(prev => {
      if (prev[district] !== undefined) {
        const next = { ...prev };
        delete next[district];
        if (editActiveDistrict === district) setEditActiveDistrict(null);
        return next;
      }
      setEditActiveDistrict(district);
      if (!jpcPrices[district]) fetchJpcPricesForUser([district]);
      // inherit common items already selected across existing districts
      const existing = Object.values(prev).find(s => s.size > 0);
      return { ...prev, [district]: existing ? new Set(existing) : new Set() };
    });
  };

  const handleEditSave = async () => {
    setError(''); setSuccess('');
    const isStateUser = editUser.jpc_type === 'state';
    if (isStateUser) {
      if (!editDistrictItems['state'] || editDistrictItems['state'].size === 0)
        return setError('Select at least one item');
    } else {
      if (Object.keys(editDistrictItems).length === 0) return setError('Select at least one district');
    }

    setEditLoading(true);
    try {
      const body = isStateUser
        ? { state: [...(editDistrictItems['state'] || [])] }
        : { districts: buildDistrictsPayload(editDistrictItems) };
      const res = await validatedFetch(`${API_BASE_URL}/user/${editUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.message || 'Failed to update user');
      setSuccess('Updated successfully');
      setEditUser(null);
      fetchUsers();
    } catch (err) { setError(safeErrorMessage(err)); }
    finally { setEditLoading(false); }
  };

  // ─────────────────────────────────────────────────────
  // delete
  // ─────────────────────────────────────────────────────

  const handleDelete = async (user) => {
    setDeleteLoading(true);
    try {
      const res = await validatedFetch(`${API_BASE_URL}/user/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'delete' }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.message || 'Failed to delete user');
      setSuccess(`${user.name} deleted successfully`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) { setError(safeErrorMessage(err)); setDeleteConfirm(null); }
    finally { setDeleteLoading(false); }
  };

  // ─────────────────────────────────────────────────────
  // derived
  // ─────────────────────────────────────────────────────

  const assignedToOthers     = getAssignedDistricts(null);
  const assignedToOthersEdit = editUser ? getAssignedDistricts(editUser.id) : new Set();

  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const getFilteredItems = (search) =>
    search.trim()
      ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
      : null;

  // ─────────────────────────────────────────────────────
  // render
  // ─────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="flex items-center justify-center w-9 h-9 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-[#1978B5] hover:border-[#1978B5] transition-all">
            <svg className="w-4 h-4" fill="none" stroke="currentC9876543210olor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">JPC User Management</h1>

          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
            <button onClick={() => setError('')} className="ml-auto">✕</button>
          </div>
        )}
        {success && (
          <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto">✕</button>
          </div>
        )}

        <div className={`grid grid-cols-1 ${showCreateForm ? 'xl:grid-cols-3' : ''} gap-6`}>

          {/* ── CREATE FORM ───────────────────────────── */}
          {showCreateForm && (
          <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-[#1978B5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Create JPC User
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">JPC Type</label>
                <select
                  value={jpcType}
                  onChange={e => handleJpcTypeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1978B5] focus:border-transparent bg-white"
                >
                  <option value="">Select type...</option>
                  <option value="state">State JPC</option>
                  <option value="regional">Regional JPC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">JPC Name</label>
                <input
                  type="text"
                  placeholder="e.g. JPC North"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1978B5] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#1978B5] focus-within:border-transparent">
                  <input
                    type="text"
                    placeholder="north"
                    value={form.usernamePrefix}
                    onChange={e => setForm(p => ({ ...p, usernamePrefix: e.target.value.replace(/\s/g, '') }))}
                    className="flex-1 px-3 py-2 text-sm focus:outline-none"
                  />
                  <span className="bg-gray-100 border-l border-gray-300 px-3 py-2 text-sm text-gray-500 font-medium select-none">@rcs</span>
                </div>
                {form.usernamePrefix && (
                  <p className="mt-1 text-xs text-gray-400">→ <span className="font-medium text-gray-600">{form.usernamePrefix}@rcs</span></p>
                )}
              </div>
            </div>

            {/* Items / Districts panel — shown only after type is selected */}
            {jpcType === 'state' ? (
              <div className="border border-gray-200 rounded-xl overflow-hidden" style={{ height: 420 }}>
                <div className="h-full p-3 flex flex-col min-h-0">
                  <ItemPanel
                    districtName="state"
                    selectedSet={districtItems['state'] || new Set()}
                    onToggle={toggleItem}
                    onSelectAll={selectAllItems}
                    search={itemSearch}
                    setSearch={setItemSearch}
                    isEdit={false}
                    groupedItems={groupedItems}
                    getFilteredItems={getFilteredItems}
                    jpcPrices={jpcPrices}
                  />
                </div>
              </div>
            ) : jpcType === 'regional' ? (
              <div className="border border-gray-200 rounded-xl overflow-hidden" style={{ height: 420 }}>
                <div className="grid grid-cols-5 h-full divide-x divide-gray-200">
                  <DistrictPanel
                    districtItemsMap={districtItems}
                    assignedOthers={assignedToOthers}
                    activeDist={activeDistrict}
                    setActiveDist={setActiveDistrict}
                    onToggle={toggleDistrict}
                    search={districtSearch}
                    setSearch={setDistrictSearch}
                  />
                  <div className="col-span-3 flex flex-col p-3 min-h-0">
                    {activeDistrict && districtItems[activeDistrict] !== undefined ? (
                      <ItemPanel
                        districtName={activeDistrict}
                        selectedSet={districtItems[activeDistrict]}
                        onToggle={toggleItem}
                        onSelectAll={selectAllItems}
                        search={itemSearch}
                        setSearch={setItemSearch}
                        isEdit={false}
                        groupedItems={groupedItems}
                        getFilteredItems={getFilteredItems}
                        jpcPrices={jpcPrices}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <svg className="w-10 h-10 mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <p className="text-sm">Select a district to assign items</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-gray-300 rounded-xl flex items-center justify-center text-gray-400 text-sm" style={{ height: 120 }}>
                Select a JPC type above to continue
              </div>
            )}

            <button
              onClick={handleCreate}
              disabled={submitLoading || loadingItems}
              className="mt-4 w-full bg-[#1978B5] hover:bg-[#146699] text-white font-medium py-2.5 rounded-lg text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {submitLoading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              )}
              {submitLoading ? 'Creating...' : 'Create JPC User'}
            </button>
          </div>
          )}

          {/* ── USER LIST ─────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col" style={{ maxHeight: showCreateForm ? 640 : undefined }}>
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-800">JPC Users</h2>
              <div className="flex items-center gap-3">
                {!showCreateForm && (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-[#1978B5] hover:bg-[#146699] text-white text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add JPC User
                  </button>
                )}
                {showCreateForm && (
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Hide form
                  </button>
                )}
              <button onClick={fetchUsers} className="text-xs text-[#1978B5] hover:underline flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              </div>
            </div>

            {loadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <svg className="w-6 h-6 animate-spin text-[#1978B5]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No JPC users found</div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-0">
                {users.map(user => {
                  const itemMap       = Object.fromEntries(items.map(i => [i.id, i.name]));
                  const isState       = user.jpc_type === 'state';
                  const stateItemIds  = isState ? (user.items || []) : [];
                  const districtNames = !isState ? Object.keys(user.districts || {}) : [];
                  const totalCount    = isState
                    ? stateItemIds.length
                    : new Set(Object.values(user.districts || {}).flatMap(arr => arr || [])).size;

                  const isExpanded = expandedUserId === user.id;
                  return (
                    <div key={user.id} className="border border-gray-100 rounded-lg hover:border-blue-200 transition-all">
                      {/* Collapsed header — always visible */}
                      <button
                        onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <p className="font-semibold text-gray-800 text-sm truncate">{user.name}</p>
                          {user.jpc_type && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${isState ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-green-50 text-green-600 border-green-100'}`}>
                              {isState ? 'State' : 'Regional'}
                            </span>
                          )}
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold flex-shrink-0">
                            {isState
                              ? `${totalCount} ${totalCount === 1 ? 'item' : 'items'}`
                              : `${districtNames.length} ${districtNames.length === 1 ? 'district' : 'districts'} · ${totalCount} ${totalCount === 1 ? 'item' : 'items'}`}
                          </span>
                          <span
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/price-updation', {
                                state: {
                                  readonly: true,
                                  jpcType: isState ? 'state' : 'regional',
                                  items: isState ? stateItemIds : null,
                                  district: !isState && districtNames.length > 0 ? districtNames[0] : null,
                                  userName: user.name,
                                },
                              });
                            }}
                            className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-200 px-2 py-0.5 rounded-full font-semibold flex-shrink-0 cursor-pointer transition-colors"
                          >
                            Rates →
                          </span>
                        </div>
                        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-1 border-t border-gray-100">
                          <p className="text-xs text-gray-400 mb-2">{user.username}</p>

                          {isState ? (
                            <div className="flex flex-wrap gap-1">
                              {stateItemIds.map(id => (
                                <span key={id} className="text-xs bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded-full">
                                  {itemMap[id] ? itemMap[id].split('/')[0].trim() : `Item #${id}`}
                                </span>
                              ))}
                            </div>
                          ) : (
                            (() => {
                              const allItemIds = [...new Set(districtNames.flatMap(d => user.districts[d] || []))];
                              return (
                                <div className="space-y-2">
                                  <div className="flex flex-wrap gap-1.5">
                                    {districtNames.map(d => (
                                      <span key={d} className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                                        {d}
                                      </span>
                                    ))}
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {allItemIds.map(id => (
                                      <span key={id} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full">
                                        {itemMap[id] ? itemMap[id].split('/')[0].trim() : `Item #${id}`}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()
                          )}

                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => openEdit(user)}
                              className="text-xs bg-gray-100 hover:bg-[#1978B5] hover:text-white text-gray-600 px-3 py-1.5 rounded-lg transition-all"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(user)}
                              className="text-xs bg-red-50 hover:bg-red-500 hover:text-white text-red-500 px-3 py-1.5 rounded-lg transition-all"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── EDIT MODAL ──────────────────────────────── */}
        {editUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col"
              style={{ height: '88vh', maxHeight: 700 }}
              onWheel={e => e.stopPropagation()}
            >
              {/* modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Edit JPC User</h3>
                  <p className="text-xs text-gray-400">{editUser.name} — <span className="font-medium">{editUser.username}</span></p>
                </div>
                {loadingPrices && (
                  <span className="text-xs text-blue-500 flex items-center gap-1 mr-2">
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Loading prices...
                  </span>
                )}
                <button onClick={() => setEditUser(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
              </div>

              {/* modal body */}
              <div className="flex-1 overflow-hidden p-4 min-h-0">
                <div className="border border-gray-200 rounded-xl overflow-hidden h-full">
                  {editUser.jpc_type === 'state' ? (
                    <div className="h-full p-3 flex flex-col min-h-0">
                      <ItemPanel
                        districtName="state"
                        selectedSet={editDistrictItems['state'] || new Set()}
                        onToggle={toggleItem}
                        onSelectAll={selectAllItems}
                        search={editItemSearch}
                        setSearch={setEditItemSearch}
                        isEdit={true}
                        groupedItems={groupedItems}
                        getFilteredItems={getFilteredItems}
                        jpcPrices={jpcPrices}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-5 h-full divide-x divide-gray-200">
                      <DistrictPanel
                        districtItemsMap={editDistrictItems}
                        assignedOthers={assignedToOthersEdit}
                        activeDist={editActiveDistrict}
                        setActiveDist={setEditActiveDistrict}
                        onToggle={toggleEditDistrict}
                        search={editDistrictSearch}
                        setSearch={setEditDistrictSearch}
                      />
                      <div className="col-span-3 flex flex-col p-3 min-h-0">
                        {editActiveDistrict && editDistrictItems[editActiveDistrict] !== undefined ? (
                          <ItemPanel
                            districtName={editActiveDistrict}
                            selectedSet={editDistrictItems[editActiveDistrict]}
                            onToggle={toggleItem}
                            onSelectAll={selectAllItems}
                            search={editItemSearch}
                            setSearch={setEditItemSearch}
                            isEdit={true}
                            groupedItems={groupedItems}
                            getFilteredItems={getFilteredItems}
                            jpcPrices={jpcPrices}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <p className="text-sm">Select a district to assign items</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* modal footer */}
              <div className="flex gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">
                <button onClick={() => setEditUser(null)} className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  disabled={editLoading}
                  className="flex-1 bg-[#1978B5] hover:bg-[#146699] text-white py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {editLoading ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── DELETE CONFIRM ────────────────────────── */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">Delete JPC User</h3>
                  <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-5">
                Are you sure you want to delete <span className="font-semibold">{deleteConfirm.name}</span> ({deleteConfirm.username})?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleteLoading}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleteLoading ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default JpcUser;